import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send } from 'lucide-react';
import { useMessages, useSendMessage } from '@/hooks/useMessages';
import { useAuth } from '@/hooks/useAuth';
import type { Message } from '@/types';
import { format, isToday, isYesterday } from 'date-fns';
import { MentionInput } from './MentionInput';

interface GroupChatProps {
  isOpen: boolean;
  onClose: () => void;
  scrollToMessageId?: string | null;
}

export function GroupChat({ isOpen, onClose, scrollToMessageId }: GroupChatProps) {
  const { user } = useAuth();
  const { data: messages = [], isLoading } = useMessages();
  const sendMessageMutation = useSendMessage();
  const [newMessage, setNewMessage] = useState('');
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  }, [messages]);

  // Scroll to bottom and focus input when opened
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
      });
    }
  }, [isOpen]);

  // Scroll to specific message (from notification click)
  useEffect(() => {
    if (scrollToMessageId && isOpen && messages.length > 0) {
      const el = document.querySelector(`[data-testid="container-chat-message-${scrollToMessageId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-blue-400');
        setTimeout(() => el.classList.remove('ring-2', 'ring-blue-400'), 2000);
      }
    }
  }, [scrollToMessageId, isOpen, messages]);

  const handleMentionsChange = useCallback((ids: string[]) => {
    setMentionedUserIds(ids);
  }, []);

  const handleSend = async () => {
    const content = newMessage.trim();
    if (!content) return;

    const mentionIds = [...mentionedUserIds];
    setNewMessage('');
    setMentionedUserIds([]);
    try {
      await sendMessageMutation.mutateAsync({
        content,
        mentioned_user_ids: mentionIds.length > 0 ? mentionIds : undefined,
      });
    } catch (err) {
      setNewMessage(content);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatMessageTime = (dateString: string) => {
    const date = new Date(dateString);
    if (isToday(date)) {
      return format(date, 'h:mm a');
    } else if (isYesterday(date)) {
      return `Yesterday ${format(date, 'h:mm a')}`;
    }
    return format(date, 'MMM d, h:mm a');
  };

  // Role group labels for detecting role mentions in message content
  const ROLE_GROUP_LABELS = ['@caretakers', '@guardians', '@family', '@all'];

  // Render message content with @mention pills
  const renderMessageContent = (
    content: string,
    mentionedIds?: string[],
    currentUserId?: string
  ): React.ReactNode => {
    if (!mentionedIds || mentionedIds.length === 0) return content;

    // Split on @word patterns
    const parts = content.split(/(@\w+)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('@')) {
        const isRoleGroup = ROLE_GROUP_LABELS.includes(part.toLowerCase());
        if (isRoleGroup) {
          return (
            <span
              key={idx}
              className="inline-block px-1.5 py-0.5 rounded text-xs font-medium bg-purple-500/30 text-purple-200"
            >
              {part}
            </span>
          );
        }
        const isSelfMention = mentionedIds.includes(currentUserId || '');
        return (
          <span
            key={idx}
            className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${
              isSelfMention ? 'bg-yellow-500/30 text-yellow-200' : 'bg-blue-500/30 text-blue-200'
            }`}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
      <div data-testid="modal-group-chat" className="bg-slate-800 border border-slate-700 w-full sm:w-[400px] h-full sm:h-[600px] sm:max-h-[80vh] sm:rounded-xl flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div>
            <h2 className="text-lg font-semibold text-white">Family Chat</h2>
            <p className="text-xs text-gray-400">Everyone in the family can see messages</p>
          </div>
          <button
            onClick={onClose}
            data-testid="clickable-chat-close"
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        {/* Messages */}
        <div data-testid="container-chat-messages" className="flex-1 overflow-y-auto p-4 space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-400">Loading messages...</div>
            </div>
          ) : messages.length === 0 ? (
            <div data-testid="container-chat-empty" className="flex items-center justify-center h-full">
              <div className="text-center text-gray-400">
                <p className="mb-2">No messages yet</p>
                <p className="text-sm">Be the first to say hello!</p>
              </div>
            </div>
          ) : (
            messages.map((message: Message) => {
              const isOwnMessage = message.user_id === user?.id;
              const sender = message.user;

              return (
                <div
                  key={message.id}
                  data-testid={`container-chat-message-${message.id}`}
                  className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] ${
                      isOwnMessage ? 'order-2' : 'order-1'
                    }`}
                  >
                    {/* Sender info - only show for others */}
                    {!isOwnMessage && (
                      <div className="flex items-center gap-1.5 mb-1 ml-1">
                        {sender?.emoji && (
                          <span className="text-sm">{sender.emoji}</span>
                        )}
                        <span
                          className="text-xs font-medium"
                          style={{ color: sender?.color || '#9ca3af' }}
                        >
                          {sender?.first_name || 'Unknown'}
                        </span>
                      </div>
                    )}

                    {/* Message bubble */}
                    <div
                      className={`px-3 py-2 rounded-2xl ${
                        isOwnMessage
                          ? 'rounded-br-md'
                          : 'rounded-bl-md'
                      }`}
                      style={{
                        backgroundColor: isOwnMessage
                          ? (user?.color || '#3b82f6')
                          : 'rgba(51, 65, 85, 0.8)',
                        borderLeft: !isOwnMessage && sender?.color
                          ? `3px solid ${sender.color}`
                          : undefined,
                      }}
                    >
                      <p className="text-white text-sm whitespace-pre-wrap break-words">
                        {renderMessageContent(message.content, message.mentioned_user_ids, user?.id)}
                      </p>
                    </div>

                    {/* Time */}
                    <div
                      className={`text-[10px] text-gray-500 mt-1 ${
                        isOwnMessage ? 'text-right mr-1' : 'ml-1'
                      }`}
                    >
                      {formatMessageTime(message.created_at)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center gap-2">
            <MentionInput
              value={newMessage}
              onChange={setNewMessage}
              onMentionsChange={handleMentionsChange}
              onKeyDown={handleKeyDown}
              disabled={sendMessageMutation.isPending}
              placeholder="Type a message... (@mention)"
            />
            <button
              onClick={handleSend}
              disabled={!newMessage.trim() || sendMessageMutation.isPending}
              className="p-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="clickable-chat-send"
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GroupChat;
